import React from "react";
import { scrollToTop } from "../utils/scrollToTop";
import { MAX_DURATION_VIDEO_EDITOR } from "../constants";

interface LandingProps {
  onStartVideo: () => void;
  onStartThumbnail: () => void;
  onStartBanner: () => void;
  onStartVideoEditor: () => void;
  onStartAutoCaptions: () => void;
  onStartShortsClipper: () => void;
  onStartAllInOne: () => void;
  onStartSimpleComposer2: () => void;
  onStartGifCreator: () => void;
  onTutorialsClick?: () => void;
}

export function Landing({ onStartVideo, onStartThumbnail, onStartBanner, onStartVideoEditor, onStartAutoCaptions,
   onStartShortsClipper, onStartAllInOne, onStartSimpleComposer2, onStartGifCreator, onTutorialsClick }: LandingProps) {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <main className="max-w-7xl mx-auto px-4 py-8 sm:py-12 lg:pt-24 lg:pb-16 text-center">
      {/* Hero Section */}
      <header>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3 sm:mb-4 text-gray-900">
          Create Viral Shorts in Minutes
        </h1>
        <p className="text-gray-600 mb-4 sm:mb-6 text-base sm:text-lg max-w-2xl mx-auto px-2">
          Free browser-based video editor for YouTube Shorts, TikTok, and Instagram Reels. 
          Add captions, edit videos, and create professional content with transitions.
        </p>
      </header>
      
      {/* Privacy Notice */}
      <aside className="max-w-2xl mx-auto mb-6 sm:mb-8 lg:mb-10 px-2" aria-label="Privacy notice">
        <div className="inline-flex items-center gap-2 px-3 py-2 sm:px-6 sm:py-3 bg-green-50 border-2 border-green-200 rounded-full">
          <span className="text-xl sm:text-2xl" role="img" aria-label="Lock">🔒</span>
          <span className="text-green-700 font-semibold text-base sm:text-lg">
            100% Private - All processing happens in your browser. Your files never leave your device.
          </span>
        </div>
      </aside>
      
      {/* Tools Grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 max-w-7xl mx-auto" aria-label="Video editing tools">
        {/* TIER 1: CRITICAL FEATURES */}
        
        {/* Caption Creator - #1 PRIORITY */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 hover:bg-gray-50 transition-colors relative border border-gray-200 shadow-sm flex flex-col" itemScope itemType="https://schema.org/SoftwareApplication">
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-blue-600 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-sm font-bold">
            Essential
          </div>
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">💬</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">Caption Creator</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
            Add professional captions to your shorts. Boost engagement by 40-80%!
            Manual editing with full styling or AI transcription.
          </p>
          <button
            onClick={() => {
              scrollToTop();
              onStartAutoCaptions();
            }}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-blue-600 text-white hover:bg-blue-700 text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Add Captions
          </button>
        </article>

        {/* Video Stack Composer - moved up */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 hover:bg-gray-50 transition-colors relative border border-gray-200 shadow-sm flex flex-col" itemScope itemType="https://schema.org/SoftwareApplication">
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-green-600 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-sm font-bold">
            New!
          </div>
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🎥</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">Video Stack Composer</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
            Easy video stacking! Stack videos side-by-side, overlay, or top-bottom. 
            Add text, emojis, and images. Simple and fast.
          </p>
          <button
            onClick={() => {
              scrollToTop();
              onStartSimpleComposer2();
            }}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-green-600 text-white hover:bg-green-700 text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Start Video Stack Composer
          </button>
        </article>

        {/* Video Composer - #3 PRIORITY */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 hover:bg-gray-50 transition-colors relative border border-gray-200 shadow-sm flex flex-col" itemScope itemType="https://schema.org/SoftwareApplication">
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-blue-600 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-sm font-bold">
            Essential
          </div>
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🎬</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">Video Composer</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
          Full timeline editor with audio, overlays, and transitions—create complete 
          Shorts right in your browser.
          </p>
          <button
            onClick={() => {
              scrollToTop();
              onStartAllInOne();
            }}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-blue-600 text-white hover:bg-blue-700 text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Launch Video Composer
          </button>
        </article>

        {/* Edit Video - moved down */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm flex flex-col" itemScope itemType="https://schema.org/SoftwareApplication">
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">✂️</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">Edit Video</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
            Trim, cut, and add overlays to videos up to {MAX_DURATION_VIDEO_EDITOR / 60} minutes. 
            Extract audio, add effects with timeline view.
          </p>
          <button
            onClick={() => {
              scrollToTop();
              onStartVideoEditor();
            }}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-black text-white hover:opacity-90 text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Edit Video
          </button>
        </article>

        {/* Simple Composer (Legacy) */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm flex flex-col">
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">📽️</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">Simple Composer</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
            Simple slide-based editor. Stitch images & clips with basic text overlays.
          </p>
          <button
            onClick={() => {
              scrollToTop();
              onStartVideo();
            }}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-black text-white hover:opacity-90 text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Start Simple Composer
          </button>
        </article>

        {/* TIER 2: HIGH VALUE */}

        {/* Shorts Clipper - NOW AVAILABLE */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 hover:bg-gray-50 transition-colors relative border border-gray-200 shadow-sm flex flex-col">
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-green-600 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-sm font-bold">
            New!
          </div>
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🎯</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">Shorts Clipper</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
            Turn long videos into multiple Shorts clips. 
            Auto-detect highlights, crop to vertical, batch export.
          </p>
          <button
            onClick={() => {
              scrollToTop();
              onStartShortsClipper();
            }}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-green-600 text-white hover:bg-green-700 text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Extract Clips
          </button>
        </article>

        {/* Thumbnail Creator */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm flex flex-col">
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🖼️</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">Thumbnail Creator</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
            Design eye-catching thumbnails for YouTube Shorts.
            Custom backgrounds, text, images, and SVG shapes.
          </p>
          <button
            onClick={() => {
              scrollToTop();
              onStartThumbnail();
            }}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-black text-white hover:opacity-90 text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Design Thumbnail
          </button>
        </article>

        {/* Templates */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm relative flex flex-col">
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-sm font-bold">
            Coming Soon
          </div>
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3 opacity-60">📋</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-700 h-8 sm:h-10">Templates</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
            Professional Shorts templates ready to use. 
            Intros, hooks, tutorials - just add your content.
          </p>
          <button
            disabled
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-gray-300 text-gray-500 cursor-not-allowed text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Coming Soon
          </button>
        </article>

        {/* TIER 3: NICE TO HAVE */}

        {/* GIF Creator */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm relative flex flex-col">
          {process.env.NODE_ENV === 'development' ? (
            <>
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🎞️</div>
              <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">GIF Creator</h2>
              <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
                Convert videos to optimized GIFs with frame control 
                and platform optimization for social media.
              </p>
              <button
                onClick={() => {
                  scrollToTop();
                  onStartGifCreator();
                }}
                className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-purple-600 text-white hover:bg-purple-700 text-base sm:text-lg font-semibold h-11 sm:h-14"
              >
                Create GIF
              </button>
            </>
          ) : (
            <>
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-sm font-bold">
                Coming Soon
              </div>
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-3 opacity-60">🎞️</div>
              <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-700 h-8 sm:h-10">GIF Creator</h2>
              <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
                Convert videos to optimized GIFs with frame control 
                and platform optimization for social media.
              </p>
              <button
                disabled
                className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-gray-300 text-gray-500 cursor-not-allowed text-base sm:text-lg font-semibold h-11 sm:h-14"
              >
                Coming Soon
              </button>
            </>
          )}
        </article>

        {/* Banner Creator */}
        <article className="bg-white rounded-2xl p-4 sm:p-6 hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm flex flex-col">
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🎨</div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2 h-8 sm:h-10">YouTube Banner</h2>
          <p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg h-20 sm:h-20">
            Design channel banners with perfect 2048×1152 dimensions. 
            Add text, images, and shapes for channel branding.
          </p>
          <button
            onClick={() => {
              scrollToTop();
              onStartBanner();
            }}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-2xl bg-black text-white hover:opacity-90 text-base sm:text-lg font-semibold h-11 sm:h-14"
          >
            Design Banner
          </button>
        </article>
      </section>

      {/* How It Works Section */}
      <section className="mt-16 lg:mt-24 max-w-6xl mx-auto" aria-label="How it works">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-gray-900">
            How Snip Rush Works
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Create professional videos in 4 simple steps. No downloads, no signup required.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📤</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">1. Upload</h3>
            <p className="text-gray-600 text-sm">
              Drag and drop your video files directly into your browser
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">✂️</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">2. Edit</h3>
            <p className="text-gray-600 text-sm">
              Use our intuitive tools to trim, add captions, and enhance your content
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚡</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">3. Process</h3>
            <p className="text-gray-600 text-sm">
              Our browser-based engine renders your video instantly
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💾</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">4. Download</h3>
            <p className="text-gray-600 text-sm">
              Get your finished video ready for social media platforms
            </p>
          </div>
        </div>

        {/* Key Features */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 border border-gray-200 shadow-sm">
          <h3 className="text-2xl font-bold text-center mb-8">Why Choose Snip Rush?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl">🔒</span>
              </div>
              <h4 className="font-semibold mb-2">100% Private</h4>
              <p className="text-sm text-gray-600">
                All processing happens in your browser. Your files never leave your device.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl">⚡</span>
              </div>
              <h4 className="font-semibold mb-2">Lightning Fast</h4>
              <p className="text-sm text-gray-600">
                No uploads, no waiting. Edit and export videos in seconds.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-xl">🎯</span>
              </div>
              <h4 className="font-semibold mb-2">Social Media Ready</h4>
              <p className="text-sm text-gray-600">
                Optimized for YouTube Shorts, TikTok, and Instagram Reels.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <p className="text-lg text-gray-600 mb-6">
            Ready to create your first viral short?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                scrollToTop();
                onStartAutoCaptions();
              }}
              className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Start with Captions
            </button>
            <button
              onClick={() => {
                scrollToTop();
              onStartAllInOne();
              }}
              className="px-8 py-4 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Try Video Composer
            </button>
            {onTutorialsClick && (
              <button
                onClick={() => {
                  scrollToTop();
                  onTutorialsClick();
                }}
                className="px-8 py-4 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
              >
                View All Tutorials
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section className="mt-16 lg:mt-24 max-w-6xl mx-auto" aria-label="Learn more">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-gray-900">
            Learn More
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Master video editing with our comprehensive tutorials and guides.
          </p>
        </div>

        {/* Tutorials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* Caption Tutorial */}
          <article className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 hover:bg-gray-50 transition-colors">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">💬</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">Caption Mastery</h3>
            <p className="text-gray-600 mb-4 text-sm h-20">
              Learn to create engaging captions that boost engagement by 40-80%. 
              Master timing, styling, and placement techniques.
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span>Auto-transcription setup</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span>Manual timing adjustments</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span>Style customization</span>
              </div>
            </div>
            <button
              onClick={() => {
                scrollToTop();
                onStartAutoCaptions();
              }}
              className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Try Caption Creator
            </button>
          </article>

          {/* Video Editing Tutorial */}
          <article className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 hover:bg-gray-50 transition-colors">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">✂️</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">Video Editing Basics</h3>
            <p className="text-gray-600 mb-4 text-sm h-20">
              Master the fundamentals of video editing. Learn to trim, cut, 
              and add effects to create professional-looking content.
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span>Timeline navigation</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span>Precise cutting techniques</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span>Overlay management</span>
              </div>
            </div>
            <button
              onClick={() => {
                scrollToTop();
                onStartVideoEditor();
              }}
              className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Start Video Editor
            </button>
          </article>

          {/* Advanced Composer Tutorial */}
          <article className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 hover:bg-gray-50 transition-colors">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">🎬</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">Advanced Composition</h3>
            <p className="text-gray-600 mb-4 text-sm h-20">
              Create complex video compositions with multiple tracks, 
              transitions, and professional effects.
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Multi-track editing</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Transition effects</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Audio synchronization</span>
              </div>
            </div>
            <button
              onClick={() => {
                scrollToTop();
                onStartAllInOne();
              }}
              className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Try Video Composer
            </button>
          </article>

          {/* Thumbnail Design Tutorial */}
          <article className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 hover:bg-gray-50 transition-colors">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">🖼️</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">Thumbnail Design</h3>
            <p className="text-gray-600 mb-4 text-sm h-20">
              Design eye-catching thumbnails that increase click-through rates. 
              Learn color theory, typography, and composition.
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                <span>Color psychology</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                <span>Text hierarchy</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                <span>Visual balance</span>
              </div>
            </div>
            <button
              onClick={() => {
                scrollToTop();
                onStartThumbnail();
              }}
              className="w-full mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            >
              Design Thumbnail
            </button>
          </article>

          {/* Shorts Clipping Tutorial */}
          <article className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 hover:bg-gray-50 transition-colors">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">🎯</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">Shorts Clipping</h3>
            <p className="text-gray-600 mb-4 text-sm h-20">
              Turn long videos into multiple Shorts clips. 
              Master the art of highlight detection and vertical cropping.
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span>Auto-highlight detection</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span>Vertical cropping</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span>Batch processing</span>
              </div>
            </div>
            <button
              onClick={() => {
                scrollToTop();
                onStartShortsClipper();
              }}
              className="w-full mt-4 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              Extract Clips
            </button>
          </article>

          {/* Banner Design Tutorial */}
          <article className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 hover:bg-gray-50 transition-colors">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <span className="text-2xl">🎨</span>
            </div>
            <h3 className="text-xl font-semibold mb-3">Channel Branding</h3>
            <p className="text-gray-600 mb-4 text-sm h-20">
              Design professional channel banners that represent your brand. 
              Learn about dimensions, typography, and visual hierarchy.
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                <span>Perfect dimensions (2048×1152)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                <span>Brand consistency</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                <span>Mobile optimization</span>
              </div>
            </div>
            <button
              onClick={() => {
                scrollToTop();
                onStartBanner();
              }}
              className="w-full mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Design Banner
            </button>
          </article>
        </div>

        {/* Quick Tips Section */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 border border-gray-200 shadow-sm">
          <h3 className="text-2xl font-bold text-center mb-8">Quick Tips for Success</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">⏱️</span>
              </div>
              <h4 className="font-semibold mb-2">Keep It Short</h4>
              <p className="text-sm text-gray-600">
                Shorts perform best under 60 seconds. Focus on the most engaging moments.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">👀</span>
              </div>
              <h4 className="font-semibold mb-2">Hook Early</h4>
              <p className="text-sm text-gray-600">
                Capture attention in the first 3 seconds. Use captions and visual effects.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">📱</span>
              </div>
              <h4 className="font-semibold mb-2">Vertical First</h4>
              <p className="text-sm text-gray-600">
                Design for mobile viewing. Vertical format gets 3x more engagement.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🎯</span>
              </div>
              <h4 className="font-semibold mb-2">Test & Iterate</h4>
              <p className="text-sm text-gray-600">
                Try different styles and formats. Analyze what works for your audience.
              </p>
            </div>
          </div>
        </div>
      </section>
      </main>
    </div>
  );
}

